import React, { useState, useEffect, useRef } from 'react';
import { getAccessToken } from "../../services/authApi";
import { fieldReader, fieldWriter } from "../../services/loanApi";
import '../../common.css';
import Loading from "../layout/Loading";
import { info, error as logAppInsightError } from '../../utils/logger';
import { logError } from "../../utils/logError";

const TPOComment = () => {
    const [loading, setLoading] = useState(true);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");
    const [Username, setUsername] = useState("");
    const [authToken, setAuthToken] = useState(null);
    const [loanId, setLoanId] = useState(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();

            initializeAuthObject();
        } else {
            console.warn("elli.script.guest is not available.");
        }
    }, []);


    const initializeAuthObject = async () => {
        try {
            window.elli.script.getObject("loan").then(async function (loanObject) {
                loanObject.all().then(async function (loan) {
                    setLoanId(loan.id);
                    info("TPO Comment page loaded", { loanid: loan.id || "N/A" });
                    await fetchComment(loan.id);
                });
            });
        } catch (error) {
            console.error("Auth block Error:", error);
        }
    };

    const fetchComment = async (id) => {
        try {
            let loanid = id;

            const tpoAuthObject = await window.elli.script.getObject("auth");
            tpoAuthObject.getUser().then(async function (user) {
                try {
                    console.log(user);
                    setUsername(user.firstName + " " + user.lastName);
                } catch (error) {
                    console.error('Error generating Auth Code:', error);
                }
            });

            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);
            if (token) {
                setAuthToken(token);

                const fieldsToFetch = ["CX.TPO.LOANNOTES"];
                const response = await fieldReader(token, fieldsToFetch, loanid);

                const rawComments = response["CX.TPO.LOANNOTES"] || "";
                let parsedComments = [];

                if (rawComments.trim()) {
                    parsedComments = rawComments.split("~#~").map((entry) => {
                        const [author, timestamp, text] = entry.split("#$#");
                        return { author, timestamp, text };
                    });
                }

                parsedComments = parsedComments.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

                setComments(parsedComments);

                const fieldData = [
                    {
                        id: "CX.TPO.LOANNOTES.UNREAD.BROK",
                        value: "N"
                    },
                    {
                        id: "CX.BROK.UNREADMSG.COUNT",
                        value: 0
                    }
                ];
                await fieldWriter(token, loanid, fieldData);
                console.log("Comment UNREAD successfully updated in Encompass!");
            }


        } catch (error) {
            console.error("Error fetching comments:", error);

            const errorCode = logError("TPO_COMMENT_LOAD_ERROR");

            logAppInsightError("Failed to load TPO Comment", {
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to load TPO Comment"}`,
                component: "tpoComment"
            });
        } finally {
            setLoading(false);
        }
    };


    const handleAddComment = async () => {
        if (!newComment.trim()) {
            if (textareaRef.current) {
                textareaRef.current.focus();
            }
            return;
        }

        if (!authToken || !loanId) return;

        setLoading(true);

        const currentTimestamp = new Date().toLocaleString("en-US", {
            timeZone: "America/Los_Angeles",
            month: "numeric",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            hour12: true,
        });

        const newCommentEntry = {
            author: Username,
            timestamp: currentTimestamp,
            text: newComment.trim(),
        };

        const updatedComments = [newCommentEntry, ...comments];
        setComments(updatedComments);

        const updatedResponse = updatedComments
            .map((c) => `${c.author}#$#${c.timestamp}#$#${c.text}`)
            .join("~#~");

        console.log("Updated Response to be saved:", updatedResponse);

        try {
            const fieldData = [
                {
                    id: "CX.TPO.LOANNOTES",
                    value: updatedResponse,
                },
                {
                    id: "CX.TPO.LOANNOTES.UNREAD.AM",
                    value: "Y"
                }
            ];

            info("TPO Comment added", { loanid: loanId });
            await fieldWriter(authToken, loanId, fieldData);
            console.log("Comment successfully updated in Encompass!");
        } catch (error) {
            console.error("Error saving comment:", error);

            const errorCode = logError("TPO_COMMENT_SAVE_ERROR");

            logAppInsightError("Failed to save TPO Comment", {
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to save TPO Comment"}`,
                component: "tpoComment"
            });
        }
        finally {
            setLoading(false);
        }

        setNewComment("");
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <div className="min-h-screen p-6 summary-font">
            <div className="w-1/2 mx-auto p-6 bg-white rounded-lg" >
                <div className="panel-heading font-bold">
                    Conversation Log
                </div>

                <div className="p-4">
                    <textarea
                        className="w-full p-2 border rounded-md"
                        ref={textareaRef}
                        rows="3"
                        placeholder="Write a comment..."
                        value={newComment}
                        maxLength={255}
                        onChange={(e) => setNewComment(e.target.value)}
                    />
                    <div className="flex justify-end mt-2">
                        <button
                            className="btn-custom rounded 
                        text-[#FFFFFF] bg-[#2080C3] 
                        hover:text-[#E6EEF8] hover:bg-[#5DA5D9] 
                        active:bg-white transition-colors duration-200"
                            onClick={handleAddComment}
                        >
                            Add Comment
                        </button>
                    </div>
                </div>

                <div className="space-y-3 mt-3 p-4">
                    {comments.map((comment, index) => (
                        <div key={index} className="flex justify-between items-start border-t border-l border-gray-300 shadow-[4px_4px_6px_rgba(0,0,0,0.1)] rounded-lg p-3">
                            <div className="w-4/5 items-center">
                                <p className="text-lm">{comment.author}</p>
                                <p className="text-sm text-gray-600 break-words">{comment.text}</p>
                            </div>
                            <p className="text-xs text-gray-400 whitespace-nowrap text-right">{comment.timestamp}</p>
                        </div>
                    ))}
                </div>


            </div >
        </div>
    );

};

export default TPOComment;